import { isInMcVrmPostBookingStorefrontExperiment } from '@client/redux/experiments/selectors/mcvrm-post-booking-storefront';
import { mcVrmPostBookingStorefrontSelector } from '@client/redux/experiments/selectors/mcvrm-post-booking-storefront';
import { MarkAsBookedModal } from '@components/MarkAsBookedModal';
import AnalyticsConstants from '@constants/analytics';
import { XO_SESSION_TOKEN } from '@constants/membership';
import VendorAnalytics from '@decorators/vendorAnalytics';
import { afterNextPaint } from '@helpers/after-next-paint';
import useAnalyticsContext from '@hooks/useAnalyticsContext';
import { reportServerSideExperiment } from '@redux/experiments';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { experiments } from '@settings';
import type { Booking } from '@typings/redux/bookings';
import type { Decorated } from '@typings/vendor';
import { Logger } from '@utils/logger/logger';
import MembershipModalRedux, {
	type OpenModalType,
} from '@xo-union/tk-component-membership-modal-redux';
import { CookieStorage } from 'cookie-storage';
import React, { type FC, useCallback, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { BookVendorButton } from './components/BookVendorButton';
import {
	BOOKED_BUTTON,
	BOOKED_MODAL,
	FAVORITE_UNBOOKED_MODAL,
	UNBOOKED_BUTTON,
	UNBOOKED_MODAL,
} from './constants';
import { useBookVendor } from './hooks/use-book-vendor';
import { useCreateFavorite } from './hooks/use-create-favorite';
import { useIsVendorBooked } from './hooks/use-is-vendor-booked';
import { useSyncFavorite } from './hooks/use-sync-favorite';
import { useUnbookVendor } from './hooks/use-unbook-vendor';

interface StateProps {
	bookings: Booking[];
	filled: boolean;
	isMobile: boolean;
	weddingId?: string;
	memberId: string;
}

interface DispatchProps {
	reportServerSide: (
		experimentId: Experiments.Id,
		useMemberId: boolean,
	) => void;
}

export type Props = StateProps & DispatchProps;

const BookVendor: FC<Props> = ({
	bookings,
	memberId,
	filled,
	isMobile,
	weddingId,
}) => {
	const [isBookModalOpen, setIsBookModalOpen] = useState(false);
	const [openMembershipModalType, setOpenMembershipModal] =
		useState<OpenModalType | null>(null);
	const { track } = useAnalyticsContext();
	const dispatch = useAppDispatch();
	const vendor = useAppSelector((state) => state.vendor.vendor as Decorated);

	const inMcVrmPostBookingExperiment = useAppSelector(
		isInMcVrmPostBookingStorefrontExperiment,
	);

	const mcVrmPostBookingStorefrontAssignment = useAppSelector(
		mcVrmPostBookingStorefrontSelector,
	);

	const hasReportedMcvrmExperiment = useRef(false);

	const reportMcvrmExperiment = useCallback(() => {
		if (
			mcVrmPostBookingStorefrontAssignment &&
			inMcVrmPostBookingExperiment &&
			!hasReportedMcvrmExperiment.current
		) {
			hasReportedMcvrmExperiment.current = true;
			Logger.log({
				log_message: `McVrm Post Booking Storefront Assignment: ${mcVrmPostBookingStorefrontAssignment}`,
			});
			dispatch(
				reportServerSideExperiment(
					experiments.mcVrmPostBookingStorefront,
					true,
				),
			);
		}
	}, [
		mcVrmPostBookingStorefrontAssignment,
		inMcVrmPostBookingExperiment,
		dispatch,
	]);

	const cookieStorage = new CookieStorage();
	const sessionToken = cookieStorage.getItem(XO_SESSION_TOKEN);
	const isLoggedIn = Boolean(sessionToken);
	const isVendorBooked = useIsVendorBooked(bookings, vendor.id);
	const isVendorFavorite = filled;

	const createFavorite = useCreateFavorite(
		vendor,
		sessionToken,
		track,
		dispatch,
	);
	const syncFavorite = useSyncFavorite(vendor, sessionToken, track, dispatch);
	const bookVendor = useBookVendor(
		dispatch,
		vendor,
		memberId,
		sessionToken,
		track,
		reportMcvrmExperiment,
	);
	const unbookVendor = useUnbookVendor(
		dispatch,
		vendor,
		bookings,
		sessionToken,
	);

	const buttonContent = isVendorBooked ? BOOKED_BUTTON : UNBOOKED_BUTTON;
	const modalContent = isVendorBooked
		? BOOKED_MODAL
		: isVendorFavorite
			? FAVORITE_UNBOOKED_MODAL
			: UNBOOKED_MODAL;

	const closeMembershipModal = (loggedIn = false) => {
		afterNextPaint(() => {
			setOpenMembershipModal(null);
			if (loggedIn) setIsBookModalOpen(true);
		});
	};

	// --- BOOK ACTIONS ---
	const addBookedVendor = useCallback(
		async (foundOnTheKnot?: string) => {
			const booked = await bookVendor(bookings, foundOnTheKnot);
			if (booked && !isVendorFavorite) syncFavorite(weddingId);
		},
		[bookVendor, bookings, isVendorFavorite, syncFavorite, weddingId],
	);

	const handleOpenModal = useCallback(() => {
		track({
			event: AnalyticsConstants.BUTTON_CLICKED,
			properties: {
				...new VendorAnalytics(vendor).baseEventProps(),
				build_product: 'marketplace',
				source_page: 'storefront',
				cta: 'booked?',
			},
		});
		isLoggedIn ? setIsBookModalOpen(true) : setOpenMembershipModal('SIGN_UP');
	}, [isLoggedIn, track, vendor]);

	const handleConfirm = useCallback(
		(foundOnTheKnot?: string) => {
			!isVendorBooked ? addBookedVendor(foundOnTheKnot) : unbookVendor();
		},
		[isVendorBooked, addBookedVendor, unbookVendor],
	);

	const handleCancel = useCallback(() => {
		if (!isVendorBooked && !isVendorFavorite) createFavorite(weddingId);
		setIsBookModalOpen(false);
	}, [isVendorBooked, isVendorFavorite, createFavorite, weddingId]);

	return (
		<>
			<BookVendorButton
				icon={buttonContent.icon}
				text={buttonContent.text}
				onClick={handleOpenModal}
				style={isMobile ? 'quaternary-alternative' : 'secondary-alternative'}
				{...(inMcVrmPostBookingExperiment && isVendorBooked
					? { color: 'brand' }
					: {})}
			/>

			{isBookModalOpen && (
				<MarkAsBookedModal
					isVendorBooked={isVendorBooked}
					content={modalContent}
					onClose={() => setIsBookModalOpen(false)}
					onConfirm={handleConfirm}
					onCancel={handleCancel}
				/>
			)}

			{openMembershipModalType && (
				<MembershipModalRedux
					metadata={{
						userAction: 'book vendor button',
						applicationName: 'marketplace',
					}}
					openModalType={openMembershipModalType}
					onClickLogIn={() => setOpenMembershipModal('LOG_IN')}
					onClickSignUp={() => setOpenMembershipModal('SIGN_UP')}
					onSignUpSuccess={() => closeMembershipModal(true)}
					onLogInSuccess={() => closeMembershipModal(true)}
					onClose={() => closeMembershipModal(false)}
				/>
			)}
		</>
	);
};

const mapDispatchToProps: DispatchProps = {
	reportServerSide: reportServerSideExperiment,
};

function mapStateToProps(state: Redux.State): StateProps {
	return {
		bookings: state.bookings?.bookings || [],
		filled: state.favorites.filled,
		weddingId: state.favorites.weddingId,
		isMobile: state.viewport.isMobile,
		memberId: state.membership.member?.id || '',
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(BookVendor);
